import { FillGradient, Graphics } from 'pixi.js';
import type { IGraphics, GraphicsStyle, VfxBlendMode } from './IRenderer';
import { PixiOverlayHost } from './PixiOverlayHost';
import { cancelRedrawFinalize, queueRedrawFinalize, type RedrawFinalizer } from './graphicsRedrawQueue';
import type { Parkable } from './pool/DisplayPool';

/**
 * PixiGraphics — IGraphics impl backed by a PIXI.Graphics on the
 * overlay's worldContainer. Same lazy-build pattern as PixiBarLabel:
 * mutations before ensureReady() resolves are buffered and applied
 * once the underlying Graphics exists.
 *
 * The fill state model differs between Phaser and Pixi — Phaser keeps
 * fillStyle stateful across calls, Pixi v8 uses fluent .fill() per
 * shape. The IGraphics surface uses higher-level fillRect(...,color,alpha)
 * so consumers don't see that difference.
 */
type DrawKey = ReadonlyArray<number | string>;
interface DrawOp { key: DrawKey; apply: (g: Graphics) => void }

function sameKey(a: DrawKey, b: DrawKey): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

function flatten(points: ReadonlyArray<{ x: number; y: number }>): number[] {
  const flat = new Array<number>(points.length * 2);
  for (let i = 0; i < points.length; i++) {
    flat[i * 2] = points[i].x;
    flat[i * 2 + 1] = points[i].y;
  }
  return flat;
}

export class PixiGraphics implements IGraphics, RedrawFinalizer, Parkable {
  private graphics: Graphics | null = null;
  private destroyed = false;
  // Pooled (DisplayPool) and parked at alpha 0: reads as inactive so trackers
  // that hold a stale reference prune it. `generation` counts acquisitions so
  // a holder can tell its lease from the next owner's.
  private parked = false;
  private gen = 0;

  // Shapes drawn since the last real clear, in order: the key compares a
  // redraw against what is already there; `apply` (re)draws it. Before
  // ensureReady() resolves they are the buffered ops, replayed on build.
  private ops: DrawOp[] = [];
  // Next shape a redraw after clear() is compared with (see draw()).
  private cursor = 0;
  private pendingX = 0;
  private pendingY = 0;
  private pendingVisible = true;
  private pendingAlpha = 1;
  private pendingScaleX = 1;
  private pendingScaleY = 1;
  private pendingRotation = 0;
  private pendingBlendMode: VfxBlendMode = 'normal';
  private gradients = new Set<FillGradient>();
  private depth: number;
  private screenSpace: boolean;
  private glow: boolean;
  // Optional per-layer container (Scene FX v3 M1) — takes precedence over
  // screenSpace/glow/world when attaching the graphics.
  private parent?: { addChild(child: unknown): void };

  constructor(style: GraphicsStyle = {}) {
    this.depth = style.depth ?? 15;
    this.screenSpace = style.screenSpace ?? false;
    this.glow = style.glow ?? false;
    this.parent = style.parent;
    // Glow implies additive (additive + the layer's blur = bloom).
    if (this.glow) this.pendingBlendMode = 'add';
    // Perf probe `--render-attrib 3`: the add happens a microtask later, so
    // keep the creator's stack for the probe to name the owner.
    const ownerStack = (globalThis as { __renderAttribLevel?: number }).__renderAttribLevel! >= 3
      ? new Error().stack : undefined;

    void PixiOverlayHost.ensureReady().then(() => {
      if (this.destroyed) return;
      const g = new Graphics();
      if (ownerStack) (g as Graphics & { __lvOwnerStack?: string }).__lvOwnerStack = ownerStack;
      g.zIndex = this.depth;
      g.x = this.pendingX;
      g.y = this.pendingY;
      g.visible = this.pendingVisible;
      g.alpha = this.pendingAlpha;
      g.scale.set(this.pendingScaleX, this.pendingScaleY);
      g.rotation = this.pendingRotation;
      if (this.pendingBlendMode !== 'normal') g.blendMode = this.pendingBlendMode;
      // Replay buffered draw ops.
      for (const op of this.ops) op.apply(g);
      this.cursor = this.ops.length;
      // Screen-space graphics attach to the UI layer (NOT camera-bound)
      // so vignette borders + screen flashes stay fixed regardless of
      // camera scroll. Glow attaches to the bloom layer. World-space
      // (default) attaches to worldContainer.
      if (this.parent) {
        this.parent.addChild(g);
      } else if (this.screenSpace) {
        PixiOverlayHost.addUiChild(g);
      } else if (this.glow) {
        PixiOverlayHost.addWorldGlowChild(g);
      } else {
        PixiOverlayHost.addWorldChild(g);
      }
      this.graphics = g;
    });
  }

  // ── IGraphics surface ──────────────────────────────────────────────────

  setVisible(visible: boolean): this {
    this.pendingVisible = visible;
    if (this.graphics) this.graphics.visible = visible;
    return this;
  }

  setPosition(x: number, y: number): this {
    this.pendingX = x;
    this.pendingY = y;
    if (this.graphics) {
      this.graphics.x = x;
      this.graphics.y = y;
    }
    return this;
  }

  setAlpha(alpha: number): this {
    this.pendingAlpha = alpha;
    if (this.graphics) this.graphics.alpha = alpha;
    return this;
  }

  setScale(sx: number, sy?: number): this {
    this.pendingScaleX = sx;
    this.pendingScaleY = sy ?? sx;
    if (this.graphics) this.graphics.scale.set(sx, sy ?? sx);
    return this;
  }

  setRotation(radians: number): this {
    this.pendingRotation = radians;
    if (this.graphics) this.graphics.rotation = radians;
    return this;
  }

  setBlendMode(mode: VfxBlendMode): this {
    this.pendingBlendMode = mode;
    if (this.graphics) this.graphics.blendMode = mode;
    return this;
  }

  // ── IShape-compatible surface (tween targets + chainable API) ───────────

  get x(): number { return this.graphics?.x ?? this.pendingX; }
  set x(v: number) { this.pendingX = v; if (this.graphics) this.graphics.x = v; }

  get y(): number { return this.graphics?.y ?? this.pendingY; }
  set y(v: number) { this.pendingY = v; if (this.graphics) this.graphics.y = v; }

  get rotation(): number { return this.graphics?.rotation ?? this.pendingRotation; }
  set rotation(v: number) { this.pendingRotation = v; if (this.graphics) this.graphics.rotation = v; }

  get alpha(): number { return this.graphics?.alpha ?? this.pendingAlpha; }
  set alpha(v: number) { this.pendingAlpha = v; if (this.graphics) this.graphics.alpha = v; }

  get scaleX(): number { return this.graphics?.scale.x ?? this.pendingScaleX; }
  set scaleX(v: number) { this.pendingScaleX = v; if (this.graphics) this.graphics.scale.set(v, this.scaleY); }

  get scaleY(): number { return this.graphics?.scale.y ?? this.pendingScaleY; }
  set scaleY(v: number) { this.pendingScaleY = v; if (this.graphics) this.graphics.scale.set(this.scaleX, v); }

  get active(): boolean { return !this.destroyed && !this.parked; }

  /** Times this object has been handed out by a pool (0 = never pooled). */
  get generation(): number { return this.gen; }

  /**
   * Park for reuse (DisplayPool): alpha 0 and a reset transform. Never writes
   * `visible`, `zIndex` or the parent — each of those rebuilds the stage.
   */
  park(): void {
    this.parked = true;
    this.alpha = 0;
    this.setScale(1, 1);
    this.rotation = 0;
  }

  /** Back in use: alpha 1; the new owner sets position/alpha/scale. */
  unpark(): void {
    this.parked = false;
    this.gen++;
    this.alpha = 1;
  }
  setActive(_v: boolean): this { return this; }

  setDepth(depth: number): this {
    this.depth = depth;
    if (this.graphics) this.graphics.zIndex = depth;
    return this;
  }

  /**
   * Rewind, don't wipe. The next draw calls are compared with the shapes
   * already in the Pixi Graphics; only a call that differs clears it (and
   * replays the matching prefix). A frame that ends early — fewer shapes than
   * last time — is trimmed by `finalizeRedraw()` at the start of the next
   * render (graphicsRedrawQueue, flushed by PixiOverlayHost).
   *
   * Why: Pixi v8 treats any redraw of a small (batchable) Graphics as a
   * structural change and rebuilds the instruction set of the WHOLE stage
   * that frame (`GraphicsPipe.validateRenderable` → `_buildInstructions`).
   * Mob health bars and the (empty) aggro overlay are cleared and redrawn on
   * every frame, so the stage was rebuilt on every frame, at a cost that
   * grows with the number of mobs, players and effects on screen (plan 13
   * §12, Moonfall Hollow).
   */
  clear(): void {
    if (!this.graphics) {
      // Buffered ops are pre-build — clearing them is equivalent to
      // not having drawn yet, plus a no-op on first paint. Drop ops.
      this.ops = [];
      this.cursor = 0;
      return;
    }
    if (this.ops.length === 0) return;
    this.cursor = 0;
    queueRedrawFinalize(this);
  }

  /** End of frame: drop shapes the last pass did not redraw. */
  finalizeRedraw(): void {
    if (this.graphics && this.cursor < this.ops.length) this.rebuildPrefix(this.cursor);
  }

  /**
   * One draw call: skip it when it repeats the shape already at the cursor;
   * otherwise clear back to the matching prefix and draw from here.
   */
  private draw(key: DrawKey, apply: (g: Graphics) => void): void {
    const g = this.graphics;
    if (!g) {
      this.ops.push({ key, apply });
      this.cursor = this.ops.length;
      return;
    }
    const i = this.cursor;
    if (i < this.ops.length) {
      if (sameKey(this.ops[i].key, key)) {
        this.cursor = i + 1;
        return;
      }
      this.rebuildPrefix(i);
    }
    apply(g);
    this.ops.push({ key, apply });
    this.cursor = this.ops.length;
  }

  /** Clear the Pixi Graphics and replay the first `n` shapes. */
  private rebuildPrefix(n: number): void {
    const g = this.graphics;
    if (!g) return;
    g.clear();
    this.destroyGradients();
    this.ops.length = n;
    for (let i = 0; i < n; i++) this.ops[i].apply(g);
  }

  fillRect(x: number, y: number, width: number, height: number, color: number, alpha = 1): void {
    this.draw(['rect', x, y, width, height, color, alpha], (g) => {
      g.rect(x, y, width, height).fill({ color, alpha });
    });
  }

  fillVerticalGradientRect(
    x: number,
    y: number,
    width: number,
    height: number,
    topColor: number,
    bottomColor: number,
    alpha = 1,
  ): void {
    if (width <= 0 || height <= 0) return;
    this.draw(['vgrad', x, y, width, height, topColor, bottomColor, alpha], (g) => {
      // Local 0→1 coordinates let Pixi interpolate continuously across the
      // rectangle at render time. This replaces the old handful of full-width
      // solid strips whose boundaries appeared as giant horizontal scanlines.
      const gradient = new FillGradient({
        type: 'linear',
        start: { x: 0, y: 0 },
        end: { x: 0, y: 1 },
        colorStops: [
          { offset: 0, color: topColor },
          { offset: 1, color: bottomColor },
        ],
        textureSpace: 'local',
      });
      this.gradients.add(gradient);
      g.rect(x, y, width, height).fill({ fill: gradient, alpha });
    });
  }

  fillRoundedRect(x: number, y: number, width: number, height: number, radius: number, color: number, alpha = 1): void {
    this.draw(['rrect', x, y, width, height, radius, color, alpha], (g) => {
      g.roundRect(x, y, width, height, radius).fill({ color, alpha });
    });
  }

  fillCircle(x: number, y: number, radius: number, color: number, alpha = 1): void {
    this.draw(['circle', x, y, radius, color, alpha], (g) => {
      g.circle(x, y, radius).fill({ color, alpha });
    });
  }

  fillEllipse(x: number, y: number, width: number, height: number, color: number, alpha = 1): void {
    // Pixi v8 ellipse takes half-axes; Phaser convention is full width/height.
    this.draw(['ellipse', x, y, width, height, color, alpha], (g) => {
      g.ellipse(x, y, width / 2, height / 2).fill({ color, alpha });
    });
  }

  fillPolygon(points: ReadonlyArray<{ x: number; y: number }>, color: number, alpha = 1): void {
    if (points.length < 3) return;
    const flat = flatten(points);
    this.draw(['poly', color, alpha, ...flat], (g) => {
      g.poly(flat).fill({ color, alpha });
    });
  }

  strokeRect(x: number, y: number, width: number, height: number, color: number, lineWidth: number, alpha = 1): void {
    this.draw(['srect', x, y, width, height, color, lineWidth, alpha], (g) => {
      g.rect(x, y, width, height).stroke({ color, alpha, width: lineWidth });
    });
  }

  strokeRoundedRect(x: number, y: number, width: number, height: number, radius: number, color: number, lineWidth: number, alpha = 1): void {
    this.draw(['srrect', x, y, width, height, radius, color, lineWidth, alpha], (g) => {
      g.roundRect(x, y, width, height, radius).stroke({ color, alpha, width: lineWidth });
    });
  }

  strokeCircle(x: number, y: number, radius: number, color: number, lineWidth: number, alpha = 1): void {
    this.draw(['scircle', x, y, radius, color, lineWidth, alpha], (g) => {
      g.circle(x, y, radius).stroke({ color, alpha, width: lineWidth });
    });
  }

  strokeLine(x1: number, y1: number, x2: number, y2: number, color: number, lineWidth: number, alpha = 1): void {
    this.draw(['line', x1, y1, x2, y2, color, lineWidth, alpha], (g) => {
      g.moveTo(x1, y1).lineTo(x2, y2).stroke({ color, alpha, width: lineWidth });
    });
  }

  strokePolyline(points: ReadonlyArray<{ x: number; y: number }>, color: number, lineWidth: number, alpha = 1): void {
    if (points.length < 2) return;
    const flat = flatten(points);
    this.draw(['pline', color, lineWidth, alpha, ...flat], (g) => {
      g.moveTo(flat[0], flat[1]);
      for (let i = 2; i < flat.length; i += 2) g.lineTo(flat[i], flat[i + 1]);
      g.stroke({ color, alpha, width: lineWidth });
    });
  }

  strokeArc(x: number, y: number, radius: number, startAngle: number, endAngle: number, color: number, lineWidth: number, alpha = 1): void {
    this.draw(['arc', x, y, radius, startAngle, endAngle, color, lineWidth, alpha], (g) => {
      // Pixi v8 arc(x, y, r, start, end, anticlockwise=false). Start/end
      // in radians, same convention as Phaser's arc/strokePath flow.
      // moveTo the arc's first point: without it Pixi joins the arc to the
      // previous path's last point, drawing a stray line across the world
      // (seen on the golem's armour timer ring, plan 07 VQA).
      g.moveTo(x + radius * Math.cos(startAngle), y + radius * Math.sin(startAngle))
        .arc(x, y, radius, startAngle, endAngle)
        .stroke({ color, alpha, width: lineWidth });
    });
  }

  destroy(): void {
    this.destroyed = true;
    this.ops = [];
    this.cursor = 0;
    cancelRedrawFinalize(this);
    if (this.graphics) {
      this.graphics.destroy();
      this.graphics = null;
    }
    this.destroyGradients();
  }

  private destroyGradients(): void {
    for (const gradient of this.gradients) {
      try { gradient.destroy(); } catch { /* already released */ }
    }
    this.gradients.clear();
  }
}
